// Canonical date formatters. Always en-US so server-rendered output and
// client-rendered output agree regardless of the host locale.

const LOCALE = 'en-US';

const toDate = (input: Date | string): Date =>
  typeof input === 'string' ? new Date(input) : input;

// "Mar 2"
export function formatDateShort(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
  });
}

// "Mar 2, 2025"
export function formatDateMedium(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// "March 2, 2025"
export function formatDateLong(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// "Wednesday, March 2"
export function formatDateWeekday(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// "Mar 2025" — used for member-since / circle-joined month markers
export function formatMonthYear(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, {
    month: 'short',
    year: 'numeric',
  });
}

// "6:30 PM"
export function formatTimeShort(input: Date | string): string {
  return toDate(input).toLocaleTimeString(LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// "APR" — calendar block month label
export function formatMonthShortUpper(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, { month: 'short' }).toUpperCase();
}

// "YYYY-MM-DDTHH:mm" in local time — the value shape that <input
// type="datetime-local"> expects. Used to pre-fill scheduling forms
// from an existing ISO timestamp.
export function isoToDatetimeLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// "Monday" — full weekday name
export function formatWeekdayLong(input: Date | string): string {
  return toDate(input).toLocaleDateString(LOCALE, { weekday: 'long' });
}

// Compact relative timestamp for activity feeds.
// "just now" / "5m ago" / "2h ago" / "3 days ago" / "Nov 1" past 7 days,
// "Nov 1, 2024" once it crosses a year. Matches the design's profile
// activity meta-line and the feed PostCard time chip.
export function formatRelativeShort(
  input: Date | string,
  now: Date = new Date(),
): string {
  const then = toDate(input);
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  if (then.getFullYear() === now.getFullYear()) return formatDateShort(then);
  return formatDateMedium(then);
}

// Even-tighter relative timestamp used on dense card metas (Networks/Circles
// post times, Spaces post chips). "now" / "5m" / "2h" / "3d" up to a week,
// then falls back to a Mar 2 / Mar 2, 2025 style date.
export function formatRelativeCompact(
  input: Date | string,
  now: Date = new Date(),
): string {
  const then = toDate(input);
  if (Number.isNaN(then.getTime())) return '';
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  if (then.getFullYear() === now.getFullYear()) return formatDateShort(then);
  return formatDateMedium(then);
}

// Tight relative time for admin tables — never falls back to a date.
// "5s ago" / "12m ago" / "3h ago" / "5d ago". `null` becomes "never".
// Used by the admin overview + system-status cards which need a uniform
// units-only chip in narrow columns.
export function formatRelativeUnits(
  input: Date | string | null,
  now: Date | number = new Date(),
): string {
  if (input === null) return 'never';
  const then = toDate(input).getTime();
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Human-readable elapsed duration (not "ago"). "—" for null.
// "45s" / "5m 12s" / "2h 14m". Used by the system-status outbox-lag card.
export function formatDurationShort(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

// ── Locale-aware factories ─────────────────────────────────────────────────
//
// The functions above hardcode `LOCALE = 'en-US'` for SSR/CSR parity. As
// next-intl rolls out, callers in [locale]/* routes need a locale-aware
// formatter object. `buildDateFormatters(locale, msg)` and
// `buildNumberFormatters(locale)` produce per-render bundles consumed via
// `useFormatDate()` (client) and `getFormatDate()` (server).

export interface TimeMessages {
  readonly justNow: string;
  readonly minutesAgo: (count: number) => string;
  readonly hoursAgo: (count: number) => string;
  readonly daysAgo: (count: number) => string;
  readonly now: string;
  readonly minutesShort: (count: number) => string;
  readonly hoursShort: (count: number) => string;
  readonly daysShort: (count: number) => string;
  readonly secondsAgo: (count: number) => string;
  readonly never: string;
}

export interface DateFormatters {
  readonly short: (d: Date | string) => string;
  readonly medium: (d: Date | string) => string;
  readonly long: (d: Date | string) => string;
  /** "Wednesday, March 2" — weekday + month + day. */
  readonly weekday: (d: Date | string) => string;
  /** "Monday" — weekday name only. */
  readonly weekdayName: (d: Date | string) => string;
  readonly monthYear: (d: Date | string) => string;
  /** "6:30 PM" — time only. */
  readonly timeShort: (d: Date | string) => string;
  /** "APR" — calendar-block month label, upper-cased. */
  readonly monthShortUpper: (d: Date | string) => string;
  readonly relativeShort: (d: Date | string, now?: Date) => string;
  /** Tight "now" / "5m" / "2h" / "3d" up to a week, then a short date. */
  readonly relativeCompact: (d: Date | string, now?: Date) => string;
  readonly relativeUnits: (d: Date | string | null, now?: Date | number) => string;
}

export interface NumberFormatters {
  readonly integer: (n: number) => string;
  readonly compact: (n: number) => string;
  readonly percent: (n: number, fractionDigits?: number) => string;
}

export function buildDateFormatters(
  locale: string,
  msg: TimeMessages,
): DateFormatters {
  const fmt = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, opts);
  const short = fmt({ month: 'short', day: 'numeric' });
  const medium = fmt({ year: 'numeric', month: 'short', day: 'numeric' });
  const long = fmt({ year: 'numeric', month: 'long', day: 'numeric' });
  const weekday = fmt({ weekday: 'long', month: 'long', day: 'numeric' });
  const weekdayName = fmt({ weekday: 'long' });
  const monthYear = fmt({ month: 'short', year: 'numeric' });
  const timeShort = fmt({ hour: 'numeric', minute: '2-digit', hour12: true });
  const monthShort = fmt({ month: 'short' });

  const relativeShort = (input: Date | string, now: Date = new Date()): string => {
    const then = toDate(input);
    if (Number.isNaN(then.getTime())) return '';
    const diffMs = now.getTime() - then.getTime();
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return msg.justNow;
    if (minutes < 60) return msg.minutesAgo(minutes);
    const hours = Math.round(minutes / 60);
    if (hours < 24) return msg.hoursAgo(hours);
    const days = Math.round(hours / 24);
    if (days < 7) return msg.daysAgo(days);
    return then.getFullYear() === now.getFullYear()
      ? short.format(then)
      : medium.format(then);
  };

  const relativeCompact = (input: Date | string, now: Date = new Date()): string => {
    const then = toDate(input);
    if (Number.isNaN(then.getTime())) return '';
    const diffMs = now.getTime() - then.getTime();
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return msg.now;
    if (minutes < 60) return msg.minutesShort(minutes);
    const hours = Math.round(minutes / 60);
    if (hours < 24) return msg.hoursShort(hours);
    const days = Math.round(hours / 24);
    if (days < 7) return msg.daysShort(days);
    return then.getFullYear() === now.getFullYear()
      ? short.format(then)
      : medium.format(then);
  };

  const relativeUnits = (
    input: Date | string | null,
    now: Date | number = new Date(),
  ): string => {
    if (input === null) return msg.never;
    const then = toDate(input).getTime();
    const nowMs = typeof now === 'number' ? now : now.getTime();
    const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));
    if (seconds < 60) return msg.secondsAgo(seconds);
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return msg.minutesShort(minutes);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return msg.hoursShort(hours);
    const days = Math.floor(hours / 24);
    return msg.daysShort(days);
  };

  return {
    short: (d) => short.format(toDate(d)),
    medium: (d) => medium.format(toDate(d)),
    long: (d) => long.format(toDate(d)),
    weekday: (d) => weekday.format(toDate(d)),
    weekdayName: (d) => weekdayName.format(toDate(d)),
    monthYear: (d) => monthYear.format(toDate(d)),
    timeShort: (d) => timeShort.format(toDate(d)),
    monthShortUpper: (d) => monthShort.format(toDate(d)).toUpperCase(),
    relativeShort,
    relativeCompact,
    relativeUnits,
  };
}

export function buildNumberFormatters(locale: string): NumberFormatters {
  const integer = new Intl.NumberFormat(locale);
  const compact = new Intl.NumberFormat(locale, { notation: 'compact' });
  return {
    integer: (n) => integer.format(n),
    compact: (n) => compact.format(n),
    percent: (n, fractionDigits = 0) =>
      new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(n),
  };
}
